"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, Check, ChevronDown, Code2, ListChecks, PencilLine } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/providers/auth-provider";
import { fetcher } from "@/lib/api";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import type { Locale } from "@/lib/i18n";
import { LearnAccessChip } from "@/features/learn/components/LearnAccessChip";
import type {
  LearnAccessLevel,
  LearnLesson,
  LearnModule,
  LearnPart,
  LearnTrackDetail,
  LearnViewer,
} from "@/features/learn/api";

/**
 * The syllabus by part and module - `LN-HUB01`, docs/35 §4, redrawn in
 * `LN-UI04`.
 *
 * Part headings come from `Course.phases`, which is presentation only and never
 * a gate (docs/35 §2), so a module no part lists still appears, in the
 * `unassigned` part the backend puts at the end.
 *
 * **Modules collapse** (`LN-UI01`). A track is eighteen modules and grows to a
 * hundred lessons, so an open list is a page nobody can scan. The first module
 * that has lessons opens, the rest are closed, and a module with no lessons yet
 * is not a disclosure at all. The lessons of a closed module stay in the DOM
 * behind a `max-h` clip rather than being unmounted, so the syllabus is still
 * one page of indexable content, which is what the track page is for.
 *
 * **A module with no lessons says "Being written"** (`LN-UI04`, founder's
 * choice of 2026-09-23 to show the track's real progress). It used to be a full
 * row reading "0 lessons", eleven of them in a row for Learn C; a part with
 * nothing written at all is now one dashed box listing its module names.
 *
 * **A free lesson carries no chip.** Every row of the first seven C modules
 * repeated the same green "Free"; the rows now mark only what is not free, and
 * the line under the heading says an unmarked lesson is free to read. Each row
 * also says what kind of lesson it is. The kind comes from the payload: a
 * `quiz` lesson is the module test, and a text lesson whose key is `problems`
 * (every module's problem set, the manifest's naming) is the problems page.
 *
 * The parts, modules, lessons and access chips are all in the server HTML. The
 * done ticks are not: they are per viewer, so they arrive from the same route
 * read with the session after hydration, which is what keeps this page ISR-cached
 * for a stranger.
 */
/** One entry per track, so two tracks do not share a reader's open modules. */
const OPEN_MODULES_KEY = "progsity.learn.openModules";

const ACCESS_LABEL: Record<LearnAccessLevel, "accessFree" | "accessCore" | "accessPro"> = {
  free: "accessFree",
  core: "accessCore",
  pro: "accessPro",
};

type LessonKind = "reading" | "problems" | "test";

const KIND = {
  reading: { icon: BookOpen, label: "lessonKindReading" },
  problems: { icon: Code2, label: "lessonKindProblems" },
  test: { icon: ListChecks, label: "lessonKindTest" },
} as const;

function lessonKind(lesson: LearnLesson): LessonKind {
  if (lesson.type === "quiz") return "test";
  if (lesson.href.endsWith("/problems")) return "problems";
  return "reading";
}

/** "Part Zero: before you write a single line" is a label and a title. */
function splitPartTitle(title: string): { label: string | null; heading: string } {
  const colon = title.indexOf(":");
  if (colon <= 0) return { label: null, heading: title };
  const heading = title.slice(colon + 1).trim();
  return {
    label: title.slice(0, colon).trim(),
    heading: heading.charAt(0).toUpperCase() + heading.slice(1),
  };
}

async function fetchTrackViewer(trackSlug: string): Promise<LearnViewer | null> {
  const res = await fetcher<{ track: LearnTrackDetail; viewer: LearnViewer | null }>(
    `/v1/learn/tracks/${encodeURIComponent(trackSlug)}`,
  );
  return res.success ? (res.data.viewer ?? null) : null;
}

export function LearnSyllabus({
  trackSlug,
  parts,
  locale,
}: {
  trackSlug: string;
  parts: LearnPart[];
  locale: Locale;
}) {
  const t = useTranslations("learn");
  const { isAuthenticated } = useAuth();

  const { data: viewer } = useQuery({
    queryKey: ["learn", "track", trackSlug, "viewer"],
    queryFn: () => fetchTrackViewer(trackSlug),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  const done = new Set(viewer?.completedLessonIds ?? []);

  const allModules = parts.flatMap((part) => part.modules);
  const filledModules = allModules.filter((module) => module.lessons.length > 0);
  /**
   * The module the reader is actually in: the first with a lesson they have not
   * finished, and the first module with lessons at all when they have finished
   * every one or are signed out. `done` is empty until the viewer read arrives,
   * so this is the signed-out answer on the server and the reader's own answer a
   * moment later, which is the same split the done ticks already live with.
   */
  const firstUnreadModuleId =
    (filledModules.find((module) => module.lessons.some((lesson) => !done.has(lesson.id))) ??
      filledModules[0])?.id ?? null;

  const [stored, setStored] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(`${OPEN_MODULES_KEY}:${trackSlug}`);
      setStored(raw ? (JSON.parse(raw) as Record<string, boolean>) : {});
    } catch {
      // Private mode, or a value another tab wrote badly: the default stands.
      setStored({});
    }
  }, [trackSlug]);

  const isOpenModule = (id: string) => stored?.[id] ?? id === firstUnreadModuleId;

  const remember = (next: Record<string, boolean>) => {
    setStored(next);
    try {
      window.localStorage.setItem(`${OPEN_MODULES_KEY}:${trackSlug}`, JSON.stringify(next));
    } catch {
      // The preference just does not survive the tab.
    }
  };
  const toggleModule = (id: string) => remember({ ...(stored ?? {}), [id]: !isOpenModule(id) });
  const allOpen = filledModules.length > 0 && filledModules.every((module) => isOpenModule(module.id));
  const toggleAll = () =>
    remember(Object.fromEntries(filledModules.map((module) => [module.id, !allOpen])));

  const n = (value: number, digits = 1) =>
    formatNumber(value, locale, { minimumIntegerDigits: digits, useGrouping: false });
  const title = (entry: { title_en: string; title_bn: string | null }) =>
    locale === "bn" && entry.title_bn ? entry.title_bn : entry.title_en;

  const summary = (module: LearnModule) => {
    const count = (level: LearnAccessLevel) =>
      module.lessons.filter((lesson) => lesson.learnAccess === level).length;
    const [free, core, pro] = [count("free"), count("core"), count("pro")];
    const parts = [t("moduleLessons", { count: n(module.lessons.length) })];
    if (core === 0 && pro === 0) parts.push(t("moduleAllFree"));
    else {
      if (free > 0) parts.push(t("moduleFree", { count: n(free) }));
      if (core > 0) parts.push(t("moduleCore", { count: n(core) }));
      if (pro > 0) parts.push(t("modulePro", { count: n(pro) }));
    }
    return parts.join(" · ");
  };

  const numberTile = (number: number, muted: boolean) => (
    <span
      aria-hidden
      className={cn(
        "flex h-10 w-10 shrink-0 items-center justify-center rounded font-mono text-[13px] font-semibold",
        muted ? "bg-neutral-100 text-neutral-500" : "bg-primary/10 text-primary-ink",
      )}
    >
      {n(number, 2)}
    </span>
  );

  return (
    <section aria-labelledby="learn-syllabus">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <h2
            className="font-[family-name:var(--font-heading)] text-2xl font-bold tracking-tight text-[rgb(var(--foreground))] lg:text-3xl"
            id="learn-syllabus"
          >
            {t("syllabusHeading")}
          </h2>
          <p className="mt-2 text-[15px] leading-relaxed text-neutral-600">
            {t("syllabusIntro", {
              modules: n(allModules.length),
              parts: n(parts.length),
              out: n(filledModules.length),
            })}{" "}
            {t("syllabusUnmarked")}
          </p>
        </div>
        {filledModules.length > 1 ? (
          <Button
            aria-pressed={allOpen}
            className="h-10 shrink-0 self-start border border-[rgb(var(--border))] bg-white text-[rgb(var(--foreground))] sm:self-auto"
            onClick={toggleAll}
            type="button"
            variant="tertiary"
          >
            {allOpen ? t("collapseAll") : t("expandAll")}
            <ChevronDown aria-hidden className={cn("h-4 w-4 transition-transform", allOpen && "rotate-180")} />
          </Button>
        ) : null}
      </div>

      <div className="mt-2">
        {parts.map((part) => {
          const { label, heading } = splitPartTitle(title(part));
          const partLessons = part.modules.reduce((sum, module) => sum + module.lessons.length, 0);
          const anyFilled = partLessons > 0;
          return (
            <section aria-labelledby={`part-${part.key}`} className="mt-10 lg:mt-11" key={part.key}>
              <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
                <div>
                  {label ? (
                    <p className="font-[family-name:var(--font-heading)] text-xs font-bold uppercase tracking-[0.14em] text-primary-ink">
                      {label}
                    </p>
                  ) : null}
                  <h3
                    className="mt-1.5 font-[family-name:var(--font-heading)] text-lg font-bold leading-snug text-[rgb(var(--foreground))] lg:text-xl"
                    id={`part-${part.key}`}
                  >
                    {heading}
                  </h3>
                </div>
                <span className="shrink-0 text-[13px] text-neutral-500">
                  {t("countModules", { count: n(part.modules.length) })}
                  {anyFilled ? ` · ${t("countLessons", { count: n(partLessons) })}` : ""}
                </span>
              </div>

              {anyFilled ? (
                <ol className="mt-4 space-y-2.5">
                  {part.modules.map((module) => {
                    const number = allModules.indexOf(module);
                    if (module.lessons.length === 0) {
                      return (
                        <li
                          className="flex items-center gap-4 rounded-[var(--radius)] border border-dashed border-neutral-300 bg-neutral-50 px-4 py-3 sm:px-5"
                          key={module.id}
                        >
                          {numberTile(number, true)}
                          <span className="min-w-0 flex-1">
                            <span className="sr-only">{t("moduleNumber", { number: n(number) })}: </span>
                            <span className="font-[family-name:var(--font-heading)] text-[15px] font-medium text-neutral-600">
                              {title(module)}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-1.5 text-xs text-neutral-500">
                            <PencilLine aria-hidden className="h-3.5 w-3.5" />
                            {t("beingWritten")}
                          </span>
                        </li>
                      );
                    }
                    const isOpen = isOpenModule(module.id);
                    return (
                      <li
                        className={cn(
                          "overflow-hidden rounded-[var(--radius)] border bg-white",
                          isOpen ? "border-primary/30" : "border-[rgb(var(--border))]",
                        )}
                        key={module.id}
                      >
                        <h4>
                          <Button
                            aria-controls={`module-${module.id}`}
                            aria-expanded={isOpen}
                            className={cn(
                              // `whitespace-normal` undoes the primitive's `nowrap`:
                              // a module title is a sentence, and on a 375 px screen
                              // one line of it pushed the page 153 px wide
                              // (`LN-UI02`, found on the founder's phone).
                              "flex h-auto w-full items-center justify-start gap-4 whitespace-normal rounded-none px-4 py-3.5 text-left sm:px-5",
                              isOpen && "bg-primary/[0.04]",
                            )}
                            onClick={() => toggleModule(module.id)}
                            type="button"
                            variant="tertiary"
                          >
                            {numberTile(number, false)}
                            <span className="min-w-0 flex-1">
                              <span className="sr-only">{t("moduleNumber", { number: n(number) })}: </span>
                              <span className="block font-[family-name:var(--font-heading)] text-[15px] font-bold leading-snug text-[rgb(var(--foreground))] lg:text-base">
                                {title(module)}
                              </span>
                              <span className="mt-0.5 block text-xs font-normal text-neutral-500">
                                {summary(module)}
                              </span>
                            </span>
                            <ChevronDown
                              aria-hidden
                              className={cn(
                                "h-[18px] w-[18px] shrink-0 text-neutral-500 transition-transform",
                                isOpen && "rotate-180",
                              )}
                            />
                          </Button>
                        </h4>

                        <ol
                          className={cn(
                            "overflow-hidden transition-[max-height] duration-200 ease-out",
                            isOpen ? "max-h-[3000px]" : "max-h-0",
                          )}
                          id={`module-${module.id}`}
                        >
                          {module.lessons.map((lesson, lessonIndex) => {
                            const isDone = done.has(lesson.id);
                            const kind = KIND[lessonKind(lesson)];
                            const KindIcon = kind.icon;
                            return (
                              <li
                                className="flex items-start gap-3 border-t border-neutral-100 px-4 py-3 sm:items-center sm:gap-3.5 sm:py-2.5 sm:pl-[4.75rem] sm:pr-5"
                                key={lesson.id}
                              >
                                <span
                                  aria-hidden
                                  className={cn(
                                    "mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-[1.5px] sm:mt-0",
                                    isDone
                                      ? "border-primary bg-primary text-[rgb(var(--primary-foreground))]"
                                      : "border-neutral-300",
                                  )}
                                >
                                  {isDone ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                                </span>
                                <span className="hidden w-5 shrink-0 font-mono text-xs text-neutral-500 sm:inline">
                                  {n(lessonIndex + 1)}
                                </span>
                                <span className="flex min-w-0 flex-1 flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                                  <Link
                                    className="min-w-0 flex-1 text-sm leading-snug text-[rgb(var(--foreground))] hover:text-primary-ink hover:underline"
                                    href={`/${locale}${lesson.href}`}
                                  >
                                    {title(lesson)}
                                  </Link>
                                  <span className="flex shrink-0 flex-wrap items-center gap-2.5">
                                    {lesson.learnAccess !== "free" ? (
                                      <LearnAccessChip
                                        label={t(ACCESS_LABEL[lesson.learnAccess])}
                                        level={lesson.learnAccess}
                                      />
                                    ) : null}
                                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-neutral-500">
                                      <KindIcon aria-hidden className="h-3.5 w-3.5" />
                                      {t(kind.label)}
                                    </span>
                                    {lesson.minutes !== null ? (
                                      <span className="text-xs tabular-nums text-neutral-500">
                                        {lesson.minutes} min
                                      </span>
                                    ) : null}
                                  </span>
                                </span>
                              </li>
                            );
                          })}
                        </ol>
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="mt-4 rounded-[var(--radius)] border border-dashed border-neutral-300 bg-neutral-50 px-4 py-4 sm:px-5">
                  <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-500">
                    <PencilLine aria-hidden className="h-3.5 w-3.5" />
                    {t("beingWritten")}
                  </p>
                  <ul className="mt-3 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
                    {part.modules.map((module) => (
                      <li className="flex gap-2.5 text-sm text-neutral-600" key={module.id}>
                        <span className="pt-0.5 font-mono text-xs text-neutral-500">
                          {n(allModules.indexOf(module), 2)}
                        </span>
                        {title(module)}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </section>
  );
}
